import { useEffect, useRef } from 'react'
import { useNavigate, useParams, useLocation, Navigate } from 'react-router-dom'
import { useWorkspace } from '../../../contexts/WorkspaceContext.tsx'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { subscribeDescriptionSaga, unsubscribeDescriptionSaga, fetchTaskByNumberSaga, prefetchDescriptionSaga } from '../../../store/tasks/tasksSlice'
import { selectTaskIdByNumber, selectTasksLoading, selectTasksInitialized, selectTasksCompletedInitialized } from '../../../store/tasks/tasksSelectors'
import { selectProjectById, selectProjectsLoading } from '../../../store/projects/projectsSelectors'
import { selectStatuses, selectMembers, selectMembersInitialized } from '../../../store/workspace/workspaceSelectors'
import { selectAuth } from '../../../store/auth/authSelectors'
import { TaskProvider } from '../../../contexts/TaskContext.tsx'
import TaskModal from '../../modals/TaskModal/TaskModal.tsx'
import WorkspaceNotFoundPage from '../WorkspaceNotFoundPage/WorkspaceNotFoundPage.tsx'
import Modal from '../../modals/Modal/Modal.tsx'
import { computeTaskModalReady } from './taskModalReady'

const WorkspaceTaskModalRoute = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const location = useLocation()
    const { workspace: workspaceUrlId, taskId: taskIdParam } = useParams<{ workspace: string; taskId: string }>()
    const { workspace } = useWorkspace()

    // Parse taskId: PREFIX-NUMBER -> NUMBER
    const parseTaskNumber = (param: string | undefined): number | null => {
        if (!param) return null
        const dashIndex = param.indexOf('-')
        if (dashIndex > 0) {
            const num = parseInt(param.substring(dashIndex + 1), 10)
            return isNaN(num) ? null : num
        }
        return null
    }

    const taskNumber = parseTaskNumber(taskIdParam)

    const taskId = useAppSelector(
        selectTaskIdByNumber(workspace?.id ?? '', taskNumber ?? -1)
    )
    const loading = useAppSelector(selectTasksLoading)
    const initialized = useAppSelector(selectTasksInitialized)
    const completedInitialized = useAppSelector(selectTasksCompletedInitialized)
    const projectsLoading = useAppSelector(selectProjectsLoading)
    const task = useAppSelector(state => taskId ? state.tasks.byId[taskId] : undefined)
    const project = useAppSelector(state => task?.projectId ? selectProjectById(state, task.projectId) : undefined)
    const auth = useAppSelector(selectAuth)
    const statuses = useAppSelector(selectStatuses)
    const members = useAppSelector(selectMembers)
    const membersInitialized = useAppSelector(selectMembersInitialized)
    const deepLinkPending = useAppSelector(state => state.tasks.deepLinkFetchPending)

    // A genuine cold deep link has no backgroundLocation in history state (an in-app open from a
    // list always passes one). Captured ONCE on mount and frozen: the synthesized-backgroundLocation
    // stamp below sets location.state, which would otherwise flip this to false mid-open — disabling
    // needsFullMinimum and letting the card paint in parts. Freezing keeps the full-card gate active
    // for the whole deep-link session (SCD-903).
    const isDeepLinkRef = useRef<boolean | null>(null)
    if (isDeepLinkRef.current === null) {
        isDeepLinkRef.current = !(location.state as { backgroundLocation?: unknown } | null)?.backgroundLocation
    }
    const isDeepLink = isDeepLinkRef.current

    // The single readiness gate for opening (logic in taskModalReady.ts, unit-tested). On an authed
    // deep link there's no hover-prewarm, so opening on just task+project would render the window and
    // then visibly load the card in parts — so it waits for the full card (statuses + members +
    // description) to paint complete in one go (SCD-903). It also waits for members to initialize
    // whenever the task has an assignee, so the assignee chip never flashes "Unassigned" before the
    // real assignee resolves (SCD-916). Both the modal render AND the synthesized-backgroundLocation
    // stamp below gate on this — so the stamp (which flips isDeepLink off) can't fire before ready.
    const readyToOpen = computeTaskModalReady({
        taskId,
        hasTask: !!task,
        hasProject: !!project,
        isDeepLink,
        isAuthed: !!auth,
        statusesLoaded: statuses.length > 0,
        membersLoaded: members.length > 0,
        membersInitialized,
        descriptionReady: task?.description !== undefined,
        hasAssignee: !!task?.assigneeId,
    })

    // Subscribe to the (separately stored) description. The modal no longer waits on this —
    // it opens immediately and the static description fills in when the doc arrives.
    useEffect(() => {
        if (!taskId) return
        dispatch(subscribeDescriptionSaga(taskId))
        return () => { dispatch(unsubscribeDescriptionSaga()) }
    }, [taskId, dispatch])

    // Deep-link fast path (SCD-887): on a direct /task/PREFIX-N link load the task + project +
    // description so the card opens complete without waiting for the full task wave. Fires until
    // BOTH the task AND its description are present, so a workspace switch that clears the store
    // mid-load re-runs and never leaves the description-gated open stuck (SCD-903).
    // - Task not in the store yet → direct-fetch task + project + description.
    // - Task already in the store (eager listener delivered it, no description) → fetch ONLY the
    //   description (SCD-904) instead of re-fetching the whole task + project.
    useEffect(() => {
        if (!isDeepLink || !workspace?.id || taskNumber == null) return
        if (taskId && task?.description !== undefined) return
        if (taskId) {
            dispatch(prefetchDescriptionSaga(taskId))
        } else {
            dispatch(fetchTaskByNumberSaga({ workspaceId: workspace.id, number: taskNumber }))
        }
    }, [isDeepLink, workspace?.id, taskNumber, taskId, task?.description, dispatch])

    // On a direct task link (no backgroundLocation — fresh load / external link) the main
    // <Routes> would render nothing behind the modal. Once the task resolves, synthesize a
    // backgroundLocation pointing at the task's project and stamp it onto the current history
    // entry (replace, so no extra entry). App's <Routes> then renders that project behind the
    // modal — exactly as if the task had been opened from inside the project. fromTaskLink lets
    // handleClose route to the project on close instead of stepping back into outside history.
    // Gated on readyToOpen (not just task+project): stamping the backgroundLocation flips
    // isDeepLink off, so doing it before the full minimum is ready would disable the open gate and
    // pop the body in after the window. Stamping in lockstep with the open keeps them in one paint.
    useEffect(() => {
        if (!readyToOpen || !task) return
        const state = location.state as { backgroundLocation?: unknown } | null
        if (state?.backgroundLocation) return
        navigate(location.pathname + location.search, {
            replace: true,
            state: {
                backgroundLocation: {
                    pathname: `/${workspaceUrlId}/projects/${task.projectId}`,
                    search: '', hash: '', state: null, key: 'default',
                },
                fromTaskLink: true,
            },
        })
    }, [readyToOpen, task, location.pathname, location.search, location.state, workspaceUrlId, navigate])

    const handleClose = () => {
        const state = location.state as { backgroundLocation?: { pathname: string }; fromTaskLink?: boolean } | null

        if (state?.fromTaskLink && state.backgroundLocation) {
            // Opened by a direct link — go to the task's project rather than navigate(-1),
            // which would step back out of the app into wherever the link was opened from.
            navigate(state.backgroundLocation.pathname, { replace: true })
        } else if (state?.backgroundLocation) {
            navigate(-1)
        } else {
            navigate(`/${workspaceUrlId}/projects`)
        }
    }

    // Wait for workspace, tasks, and projects to load — and until the active
    // workspace matches the URL's :workspace. On a deep link into a non-active
    // workspace (e.g. an email notification link), the workspace switches
    // asynchronously; resolving the task against the stale (last-opened)
    // workspace would find nothing and bounce to "/" — opening the wrong
    // workspace instead of the task.
    const workspaceMatchesUrl = !workspaceUrlId || workspace?.workspaceUrlId === workspaceUrlId
    // Always need the workspace resolved and matching the URL before we can resolve the task
    // against it (a deep link into a non-active workspace switches asynchronously).
    if (!workspace || !workspaceMatchesUrl) return null

    // Fast path: open once ready (see readyToOpen) — task + project from the listeners OR the
    // deep-link direct fetch, plus the full minimum on an authed deep link, so the modal paints
    // complete in one go. Comments still load per task. See TaskModalDescription.
    if (readyToOpen && taskId) {
        return (
            <TaskProvider taskId={taskId}>
                <TaskModal
                    isOpen={true}
                    onClose={handleClose}
                />
            </TaskProvider>
        )
    }

    if (isDeepLink) {
        // Direct link: the targeted fetch resolves the one task + its project (a found task is
        // already handled by the fast path above). Wait until the fetch finishes before deciding.
        if (deepLinkPending) return null
        // Task readable but its project isn't (fetch denied) → the project is private to this user.
        if (taskId && task && auth && !project) {
            return <Navigate to={`/${workspaceUrlId}/projects/${task.projectId}`} replace />
        }
        // No task from the direct fetch. It's authoritative, but guard a transient fetch error /
        // a task the listener will still deliver: also wait for the full set before bouncing.
        if (!taskId || !task) {
            if (!completedInitialized) return null
            return (
                <Modal onClose={handleClose} maxWidth={416} minHeight={320}>
                    <WorkspaceNotFoundPage entity="task" />
                </Modal>
            )
        }
        return null
    }

    // In-app open (came from a loaded list, backgroundLocation present): the data is normally
    // already in the store and the fast path returned. Otherwise keep the original gating —
    // wait for the full task/project load before concluding anything.
    if (loading || !initialized || projectsLoading) return null
    if (!taskId) {
        // The eager listener loads only OPEN tasks; completed ones arrive a beat later. Wait for
        // the completed set before concluding "not found" so a completed-task link resolves.
        if (!completedInitialized) return null
        return (
            <Modal onClose={handleClose} maxWidth={416} minHeight={320}>
                <WorkspaceNotFoundPage entity="task" />
            </Modal>
        )
    }
    if (auth && task && !project) {
        return <Navigate to={`/${workspaceUrlId}/projects/${task.projectId}`} replace />
    }
    return null
}

export default WorkspaceTaskModalRoute
